import React, { useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { localizePath } from '../utils/localizedRoutes.js';
import { FactGrid, formatBerlin } from './VeridionShared.jsx';

const errorMessageMap = {
  AGENTIC_RAG_UNAVAILABLE: 'agenticRagUnavailable',
  AGENTIC_RAG_NOT_CONFIGURED: 'agenticRagNotConfigured',
  AGENTIC_RAG_REQUEST_FAILED: 'agenticRagRequestFailed',
};

const formatTimestamp = formatBerlin;
const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());

const AgenticRagPage = ({ embedded = false }) => {
  const { t, currentLang } = useTranslation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);

  const mapError = (value) => (value ? (t.admin?.[errorMessageMap[value]] || value) : '');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const currentUser = await fetchCurrentUser();
        setMe(currentUser.user);
        if (!currentUser.user?.roles?.includes('admin')) {
          setLoading(false);
          return;
        }

        const payload = await requestJson('/api/admin/agentic-rag/health');
        setSnapshot(payload.snapshot || null);
        setError('');
      } catch (requestError) {
        setError(requestError.code || 'AGENTIC_RAG_UNAVAILABLE');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  if (loading) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.validating}</div></main>;
  }

  if (!me || !me.roles.includes('admin')) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;
  }

  const ragStats = snapshot?.rag?.statistics || {};
  const curatedQaStats = ragStats?.services?.curated_qa || {};
  const instructionStatus = snapshot?.instruction_data_generator || {};
  const trainerStatus = snapshot?.llm_trainer || {};
  const qdrantStatus = snapshot?.qdrant || {};

  const body = (
    <>
      <section className={`site-card p-6 space-y-4 admin-hero-card ${embedded ? 'admin-panel admin-span-12' : ''}`}>
        <div className="admin-section-head">
          <div>
            <div className="editorial-kicker">{t.admin.adminKicker}</div>
            <h1 className="admin-hero-title mt-3">{t.admin.agenticRagTitle}</h1>
            <p className="site-copy">{t.admin.agenticRagSubtitle}</p>
          </div>
          <div className="text-sm text-text-secondary admin-smtm-status-meta">
            <div>{t.admin.agenticRagRuntimeMode}: {snapshot?.runtime_mode || '-'}</div>
            <div>{t.admin.agenticRagDeploymentEnv}: {snapshot?.deployment_env || '-'}</div>
            <div>{t.admin.agenticRagDocuments}: {formatInteger(ragStats?.total_documents || 0)}</div>
            <div>{t.admin.agenticRagCuratedQaPairs}: {formatInteger(curatedQaStats?.qa_pairs_loaded || 0)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {!embedded ? <a className="homepage-button-secondary" href={localizePath('/admin', currentLang)}>{t.nav?.admin}</a> : null}
          <a className="homepage-button-secondary" href="/api/admin/agentic-rag/docs" target="_blank" rel="noreferrer">{t.admin.agenticRagOpenDocs}</a>
          <a className="homepage-button-secondary" href="/admin/agentic-rag/phoenix" target="_blank" rel="noreferrer">{t.admin.agenticRagOpenPhoenix}</a>
          <a className="homepage-button-secondary" href="/dashboard" target="_blank" rel="noreferrer">{t.admin.agenticRagOpenQdrant}</a>
        </div>
        {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{mapError(error)}</div> : null}
      </section>

      <section className="site-card admin-panel admin-section is-open">
        <FactGrid items={[
          [t.admin.agenticRagBackendStatus, snapshot?.status || '-'],
          [t.admin.agenticRagPhoenixStatus, snapshot?.phoenix?.running ? (t.admin.statusConfigured) : (t.admin.statusMissing)],
          [t.admin.agenticRagQdrantStatus, qdrantStatus?.available ? (t.admin.statusConfigured) : (t.admin.statusMissing)],
          [t.admin.agenticRagLatestDataset, instructionStatus?.latest_dataset || '-'],
          [t.admin.agenticRagLatestTrainerRun, trainerStatus?.latest_run_summary || '-']
        ]} />
      </section>

      <section className="site-card p-6 space-y-4 admin-panel admin-span-12">
        <div className="admin-section-head">
          <div>
            <h2 className="text-2xl font-black">{t.admin.agenticRagKnowledgeTitle}</h2>
            <p className="site-copy">{t.admin.agenticRagKnowledgeSubtitle}</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <article className="homepage-muted-panel p-4 text-sm text-text-secondary"><div>{t.admin.agenticRagDocuments}: {formatInteger(ragStats?.total_documents || 0)}</div></article>
          <article className="homepage-muted-panel p-4 text-sm text-text-secondary"><div>{t.admin.agenticRagVectorStores}: {formatInteger(Object.keys(ragStats?.stores || {}).length)}</div></article>
          <article className="homepage-muted-panel p-4 text-sm text-text-secondary"><div>{t.admin.agenticRagCuratedQaPairs}: {formatInteger(curatedQaStats?.qa_pairs_loaded || 0)}</div></article>
          <article className="homepage-muted-panel p-4 text-sm text-text-secondary"><div>{t.admin.agenticRagKnowledgeMode}: {snapshot?.settings?.knowledge_mode || '-'}</div></article>
        </div>
        <div className="homepage-muted-panel p-4 text-sm text-text-secondary space-y-1">
          <div>{t.admin.agenticRagQdrantStatus}: {qdrantStatus?.available ? (t.admin.statusConfigured) : (t.admin.statusMissing)}</div>
          <div>{t.admin.agenticRagQdrantCollection}: {qdrantStatus?.documents_collection || '-'}</div>
          <div>{t.admin.agenticRagQdrantCuratedCollection}: {qdrantStatus?.curated_qa_collection || '-'}</div>
          <div>{t.admin.agenticRagQdrantDashboard}: {qdrantStatus?.dashboard_url || '-'}</div>
        </div>
      </section>

      <section className="site-card p-6 space-y-4 admin-panel admin-span-12">
        <div className="admin-section-head">
          <div>
            <h2 className="text-2xl font-black">{t.admin.agenticRagTrainingTitle}</h2>
            <p className="site-copy">{t.admin.agenticRagTrainingSubtitle}</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <article className="homepage-muted-panel p-4 text-sm text-text-secondary space-y-1">
            <div>{t.admin.agenticRagLatestDataset}: {instructionStatus?.latest_dataset || '-'}</div>
            <div>{t.admin.agenticRagDatasetPairs}: {formatInteger(instructionStatus?.total_pairs || 0)}</div>
            <div>{t.admin.agenticRagDatasetDocuments}: {formatInteger(instructionStatus?.documents_processed || 0)}</div>
            <div>{t.admin.agenticRagDatasetModifiedAt}: {formatTimestamp(instructionStatus?.latest_dataset_modified_at)}</div>
          </article>
          <article className="homepage-muted-panel p-4 text-sm text-text-secondary space-y-1">
            <div>{t.admin.agenticRagLatestTrainerRun}: {trainerStatus?.latest_run_summary || '-'}</div>
            <div>{t.admin.agenticRagLatestTrainerInput}: {trainerStatus?.latest_input_dataset || '-'}</div>
            <div>{t.admin.agenticRagTrainerRunModifiedAt}: {formatTimestamp(trainerStatus?.latest_run_summary_modified_at)}</div>
            <div>{t.admin.agenticRagTrainerInputModifiedAt}: {formatTimestamp(trainerStatus?.latest_input_dataset_modified_at)}</div>
          </article>
        </div>
      </section>
    </>
  );

  if (embedded) {
    return <section className="admin-panel admin-span-12 space-y-6">{body}</section>;
  }

  return <main className="site-main admin-dashboard-main space-y-6">{body}</main>;
};

export default AgenticRagPage;
